import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class TrainingLane extends BaseObj {
    public ID: number;
    public __id: number;

    public ContestantID: number;
    public __contestantid: number;

    public EventID: number;
    public __eventid: number;

    public Lane: number | null;
    public __lane: number | null;

    public TrainingLaneGroupID: number;
    public __traininglanegroupid: number;

    public Type: number;
    public __type: number;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        ContestantID?: number;
        EntityState?: EntityState;
        EventID?: number;
        Lane?: number | null;
        TrainingLaneGroupID?: number;
        Type?: number;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<TrainingLane>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('TrainingLane tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantID = values.ContestantID ?? 0;
        this.__contestantid = values.ContestantID ?? 0;
        Object.defineProperty(this, 'ContestantID', {
            get(): number {
                return this.__contestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ContestantID', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Lane = values.Lane ?? null;
        this.__lane = values.Lane ?? null;
        Object.defineProperty(this, 'Lane', {
            get(): number | null {
                return this.__lane;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Lane', this.__lane, value);
                this.__lane = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TrainingLaneGroupID = values.TrainingLaneGroupID ?? 0;
        this.__traininglanegroupid = values.TrainingLaneGroupID ?? 0;
        Object.defineProperty(this, 'TrainingLaneGroupID', {
            get(): number {
                return this.__traininglanegroupid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('TrainingLaneGroupID', this.__traininglanegroupid, value);
                this.__traininglanegroupid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Type = values.Type ?? 0;
        this.__type = values.Type ?? 0;
        Object.defineProperty(this, 'Type', {
            get(): number {
                return this.__type;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Type', this.__type, value);
                this.__type = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Lane": ${JSON.stringify(this.Lane)}` +
            ',' +
            `"TrainingLaneGroupID": ${JSON.stringify(this.TrainingLaneGroupID)}` +
            ',' +
            `"Type": ${JSON.stringify(this.Type)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of TrainingLane from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        ContestantID?: number;
        EventID?: number;
        Lane?: number | null;
        TrainingLaneGroupID?: number;
        Type?: number;
        EntityState?: 'Added' | 'Unchanged';
    }): TrainingLane {
        return new TrainingLane(values);
    }

    public static _FromServerJson(json: any): TrainingLane {
        return new TrainingLane({
            ID: json.ID,
            ContestantID: json.ContestantID,
            EventID: json.EventID,
            Lane: json.Lane ?? null,
            TrainingLaneGroupID: json.TrainingLaneGroupID,
            Type: json.Type,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of TrainingLane from another instance of TrainingLane.
     * @param other The TrainingLane you want to clone
     * @returns A cloned TrainingLane
     */
    public static FromOther(other: TrainingLane): TrainingLane {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return TrainingLane._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return TrainingLane.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'ContestantID' | 'EventID' | 'Lane' | 'TrainingLaneGroupID' | 'Type')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'TrainingLane';
}
